import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';

import {
  COLLABORATE_EXTERNAL_KEYS,
  LICENSES_TYPES,
  SUBSCRIPTION_TYPES,
  SUBSCRIPTION_STATUS,
} from './const';
import { License, Offering, Product, User, Subscription } from './types';

export const buildGetUserSubscriptionsRequest = () =>
  `${getAdminApiUrl()}/v1/user-subscriptions?filter[latest]=true`;

export const isPaidSubscriptionOnOffering = (
  products: Product[],
  licenses: License[] | null | undefined,
) =>
  products.every((product) =>
    licenses?.some(
      (license) =>
        license.productType === product.key &&
        license.type === LICENSES_TYPES.PAID,
    ),
  );

export const isPaidSubscriptionOnOfferingV2 = (
  subscription: Offering,
  subscriptions: Offering[],
) =>
  subscriptions.some(
    (current) =>
      !!current.offeringName &&
      current.offeringName === subscription.offeringName &&
      current.status === SUBSCRIPTION_STATUS.ACTIVE &&
      current.type !== SUBSCRIPTION_TYPES.TRIAL,
  );

const camelize = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-z\d]+(.)/gi, (m, chr) => chr.toUpperCase());
};

const generateProductName = (trial: Offering) => {
  if (trial.offeringExternalKey === COLLABORATE_EXTERNAL_KEYS.COLLABORATE_PRO) {
    return trial.offeringCategoryName + ' Pro';
  }
  return trial.offeringCategoryName;
};

const reducer = (
  result: { [key: string]: string | number },
  trial: Offering,
  index: number,
) => {
  result.trialsDisplayed = `${result.trialsDisplayed}${
    index !== 0 ? ', ' : ''
  }${generateProductName(trial)}`;
  result[`${camelize(generateProductName(trial))}TrialDaysRemaining`] =
    trial.daysRemaining;
  return result;
};

const generateTrialsTrackingData = (trials: Offering[]) => {
  return trials.reduce(reducer, { trialsDisplayed: '' });
};

export const getTrackingProperties = (
  user: User | null,
  trials: Offering[],
  currentProductId: string,
) => ({
  modalOpenTimeStamp: new Date(Date.now()).toISOString(),
  currentPage: currentProductId,
  currentUserEmailDomain:
    user && user.email.substring(user.email.lastIndexOf('@') + 1),
  ...generateTrialsTrackingData(trials),
});

export const addDaysToDate = (date: Date, days: number) => {
  const result = new Date(date);
  return result.setDate(result.getDate() + days);
};

export const shouldShowExpiredMessage = (trials: Offering[]) => {
  const lastTrialExpired = trials.reduce(
    (result: Offering, trial: Offering) => {
      if (result) {
        return new Date(result.expirationDate) < new Date(trial.expirationDate)
          ? trial
          : result;
      }

      return trial;
    },
  );
  const trialBannerHideDate = addDaysToDate(
    new Date(lastTrialExpired.expirationDate),
    1,
  );
  const today = new Date(Date.now()).setHours(0, 0, 0, 0);

  return today <= trialBannerHideDate;
};

export const transformUserSubscriptionsData = (
  subscriptionsData: Subscription[],
): Offering[] => {
  return subscriptionsData.map((subscription) => {
    return {
      daysRemaining: subscription.daysRemaining,
      expirationDate: subscription.endDate,
      offeringCategory: subscription.offeringCategory,
      offeringCategoryName: subscription.offeringCategoryName,
      offeringCategoryRanking: subscription.offeringCategoryRanking,
      offeringExternalKey: subscription.offeringExternalKey,
      offeringName: subscription.offeringName,
      products: subscription.products.map(({ productName, productKey }) => ({
        name: productName,
        key: productKey,
      })),
      status: subscription.status,
      type: subscription.type,
    };
  });
};
